import { FC } from 'react'
import { Users, Ship, Clock, FileWarning, UserCheck, Anchor } from 'lucide-react'
import CardInfoButton from './CardInfoButton'
import type { CardMeta, CrewDerivedKpis } from './types'

interface Props {
  loading: boolean
  kpis: CrewDerivedKpis
  onKpiClick: (kpi: string) => void
}

const kpiMeta: Record<string, CardMeta> = {
  totalCrew: {
    description: 'Total number of crew members across all vessels in the fleet.',
    endpoint: 'GET /crew/all',
    logic: 'Returns the full crew list. Dashboard counts total items.',
  },
  onboardNow: {
    description: 'Crew members currently signed on and active aboard vessels.',
    endpoint: 'GET /sign/on-off-records/all',
    logic: 'Filters records where status === "SIGNED_ON" and returns count.',
  },
  pendingApprovals: {
    description: 'Crew members awaiting approval or review by management.',
    endpoint: 'GET /crew/all',
    logic: 'Counts crew members where approvalStatus is "PENDING" or undefined.',
  },
  expiringCerts: {
    description: 'Crew certifications and documents expiring or needing attention.',
    endpoint: 'GET /crew/all (heuristic)',
    logic: 'Heuristic: counts crew with document.name containing "expir". Replace with dedicated endpoint when available.',
  },
  signedOff: {
    description: 'Total number of crew sign-off records.',
    endpoint: 'GET /sign/on-off-records/all',
    logic: 'Filters records where status === "SIGNED_OFF".',
  },
  vessels: {
    description: 'Number of active vessels in the fleet.',
    endpoint: 'GET /vessels',
    logic: 'Returns the full vessel list count.',
  },
}

const sparkPath = (color: string) => (
  <svg className='w-full h-full' viewBox='0 0 100 30' preserveAspectRatio='none'>
    <polyline fill='none' stroke={color} strokeWidth='1.5' points='0,22 15,20 30,18 45,15 60,14 75,12 90,10 100,8' />
  </svg>
)

const KpiCards: FC<Props> = ({ loading, kpis, onKpiClick }) => {
  return (
    <div className='grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5 md:gap-6'>
      {/* Total Crew */}
      <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('totalCrew')}>
        <div className='flex items-center justify-between'>
          <div className='text-[10px] text-slate-500 mb-1'>Total Crew</div>
          <CardInfoButton meta={kpiMeta.totalCrew} />
        </div>
        <div className='text-2xl font-bold text-slate-900'>{loading ? '—' : kpis.totalCrew}</div>
        <div className='text-[10px] text-blue-500 flex items-center gap-0.5 mt-1'>
          <Users className='w-3 h-3' />All crew members
        </div>
        <div className='mt-2 h-10 bg-blue-50 rounded-lg relative overflow-hidden'>
          {sparkPath('#3b82f6')}
        </div>
      </div>

      {/* Onboard Now */}
      <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('onboardNow')}>
        <div className='flex items-center justify-between'>
          <div className='text-[10px] text-slate-500 mb-1'>Onboard Now</div>
          <CardInfoButton meta={kpiMeta.onboardNow} />
        </div>
        <div className='text-2xl font-bold text-slate-900'>{loading ? '—' : kpis.onboardNow}</div>
        <div className='text-[10px] text-emerald-500 flex items-center gap-0.5 mt-1'>
          <Ship className='w-3 h-3' />Currently signed on
        </div>
        <div className='mt-2 h-10 bg-emerald-50 rounded-lg relative overflow-hidden'>
          {sparkPath('#10b981')}
        </div>
      </div>

      {/* Pending Approvals */}
      <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('pendingApprovals')}>
        <div className='flex items-center justify-between'>
          <div className='text-[10px] text-slate-500 mb-1'>Pending Approvals</div>
          <CardInfoButton meta={kpiMeta.pendingApprovals} />
        </div>
        <div className='text-2xl font-bold text-slate-900'>{loading ? '—' : kpis.pendingApprovals}</div>
        <div className='text-[10px] text-amber-500 flex items-center gap-0.5 mt-1'>
          <Clock className='w-3 h-3' />Awaiting review
        </div>
        <div className='mt-2 h-10 bg-amber-50 rounded-lg relative overflow-hidden'>
          {sparkPath('#f59e0b')}
        </div>
      </div>

      {/* Certifications */}
      <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('expiringCerts')}>
        <div className='flex items-center justify-between'>
          <div className='text-[10px] text-slate-500 mb-1'>Certifications</div>
          <CardInfoButton meta={kpiMeta.expiringCerts} />
        </div>
        <div className='text-2xl font-bold text-slate-900'>{loading ? '—' : kpis.expiringCerts}</div>
        <div className='text-[10px] text-red-500 flex items-center gap-0.5 mt-1'>
          <FileWarning className='w-3 h-3' />Expiring / needs attention
        </div>
        <div className='mt-2 h-10 bg-red-50 rounded-lg relative overflow-hidden'>
          <svg className='w-full h-full' viewBox='0 0 100 30' preserveAspectRatio='none'>
            <polyline fill='none' stroke='#ef4444' strokeWidth='1.5' points='0,25 15,20 30,22 45,15 60,18 75,10 90,12 100,5' />
          </svg>
        </div>
      </div>

      {/* Signed Off */}
      <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('signedOff')}>
        <div className='flex items-center justify-between'>
          <div className='text-[10px] text-slate-500 mb-1'>Signed Off</div>
          <CardInfoButton meta={kpiMeta.signedOff} />
        </div>
        <div className='text-2xl font-bold text-slate-900'>{loading ? '—' : kpis.signedOff}</div>
        <div className='text-[10px] text-purple-500 flex items-center gap-0.5 mt-1'>
          <UserCheck className='w-3 h-3' />Total sign-offs
        </div>
        <div className='mt-2 h-10 bg-purple-50 rounded-lg relative overflow-hidden'>
          {sparkPath('#8b5cf6')}
        </div>
      </div>

      {/* Vessels */}
      <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('vessels')}>
        <div className='flex items-center justify-between'>
          <div className='text-[10px] text-slate-500 mb-1'>Vessels</div>
          <CardInfoButton meta={kpiMeta.vessels} />
        </div>
        <div className='text-2xl font-bold text-slate-900'>{loading ? '—' : kpis.vessels}</div>
        <div className='text-[10px] text-teal-500 flex items-center gap-0.5 mt-1'>
          <Anchor className='w-3 h-3' />Active vessels
        </div>
        <div className='mt-2 h-10 bg-teal-50 rounded-lg relative overflow-hidden'>
          {sparkPath('#14b8a6')}
        </div>
      </div>
    </div>
  )
}

export default KpiCards
